import { useState } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { Lock, Sparkles } from 'lucide-react';

/**
 * Composant de bouton premium avec badge "Option" et tooltip informatif
 * Affiche le bouton de manière attrayante même sans permission pour inciter à l'activer
 * 
 * @param {object} props
 * @param {string} props.commercantId - ID du commerçant
 * @param {string} props.permission - Permission requise (ex: 'GESTION_STOCK')
 * @param {string} props.featureName - Nom de la fonctionnalité (ex: 'Gestion des Ingrédients')
 * @param {string} props.description - Description de la fonctionnalité
 * @param {array} props.benefits - Liste des bénéfices (ex: ['Suivi en temps réel', 'Alertes de rupture'])
 * @param {React.ReactNode} props.children - Contenu du bouton
 * @param {function} props.onClick - Fonction appelée au clic
 * @param {boolean} props.disabled - Désactiver le bouton
 * @param {string} props.className - Classes CSS supplémentaires
 */
const PremiumButton = ({
  commercantId,
  permission,
  featureName,
  description,
  benefits = [],
  children,
  onClick,
  disabled = false,
  className = ''
}) => {
  const { hasPermission, loading } = usePermissions(commercantId);
  const [showTooltip, setShowTooltip] = useState(false);

  // Vérifier si l'utilisateur a la permission
  const hasAccess = !loading && (hasPermission(permission) || !permission);
  // Pour la démo : si commercantId est 'demo', toujours afficher le design premium
  const needsPermission = (permission && !hasAccess) || (commercantId === 'demo' && permission);

  // Gérer le clic
  const handleClick = (e) => {
    if (needsPermission) {
      e.preventDefault();
      // Optionnel : ouvrir un modal d'information ou rediriger
      alert(`Cette fonctionnalité nécessite la permission : ${permission}\n\nContactez votre administrateur pour activer cette option.`);
      return;
    }
    if (onClick && !disabled) {
      onClick(e);
    }
  };

  return (
    <div className="relative group">
      <button
        onClick={handleClick}
        disabled={disabled || (!hasAccess && !needsPermission)}
        className={`
          ${className}
          ${needsPermission ? 'relative pr-10' : ''}
          transition-all duration-200
          ${needsPermission ? 'hover:scale-105' : ''}
        `}
        onMouseEnter={() => needsPermission && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {children}
        {needsPermission && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-yellow-500 animate-pulse">
            <Sparkles className="h-4 w-4" />
          </span>
        )}
      </button>

      {/* Tooltip premium au survol */}
      {needsPermission && showTooltip && (
        <div className="absolute z-50 mt-2 w-80 bg-white rounded-lg shadow-2xl border-2 border-yellow-300 p-4 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex items-start gap-3 mb-3">
            <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center">
              <Lock className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900 mb-1">{featureName}</h4>
              <p className="text-sm text-gray-600">{description}</p>
            </div>
          </div>

          {benefits.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-medium text-gray-500 mb-2">✨ Bénéfices :</p>
              <ul className="text-xs text-gray-600 space-y-1">
                {benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <span className="text-yellow-500">•</span>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="pt-3 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-2">
              Permission requise : <span className="font-semibold text-gray-700">{permission}</span>
            </p>
            <button
              onClick={() => {
                // Optionnel : ouvrir modal ou rediriger vers page permissions
                alert(`Pour activer cette option, contactez votre administrateur.\n\nPermission nécessaire : ${permission}`);
              }}
              className="w-full bg-gradient-to-r from-yellow-400 to-yellow-600 text-white font-medium py-2 rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <Sparkles className="h-4 w-4" />
              Découvrir cette option
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PremiumButton;

